/**
 * Firebase Authentication Service
 * Handles Firebase authentication and syncs with Django backend
 */

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
  confirmPasswordReset,
  updateProfile,
  type User as FirebaseUser,
  GoogleAuthProvider,
  signInWithPopup,
  onIdTokenChanged  // Changed from onAuthStateChanged - this also fires on token refresh!
} from 'firebase/auth';
import { auth } from './firebaseConfig';
import { apiClient, tokenManager } from '@/lib/api-client';
import { TOKEN_KEYS } from '@/config/api.config';
import type { User } from '@/services/api/authService';

/**
 * Firebase authentication service
 */
export const firebaseAuthService = {
  /**
   * Sign up with email and password
   * Creates user in Firebase and Django
   */
  async signUp(
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ): Promise<{ user: User; firebaseUser: FirebaseUser }> {
    if (!auth) {
      throw new Error('Firebase not initialized');
    }

    // 1. Create user in Firebase
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;

    // Update Firebase profile with name
    await updateProfile(firebaseUser, {
      displayName: `${firstName} ${lastName}`.trim(),
    });

    // 2. Create user in Django via backend API
    const response = await apiClient.post<{ user: User; firebase_uid: string }>(
      '/auth/firebase/signup/',
      {
        email,
        password,
        first_name: firstName,
        last_name: lastName,
      },
      { skipAuth: true }
    );

    console.log('✅ User created in Django:', response.user);

    return {
      user: response.user,
      firebaseUser,
    };
  },

  /**
   * Sign in with email and password
   * Returns Firebase user and Django user data with roles
   */
  async signIn(
    email: string,
    password: string
  ): Promise<{ user: User; firebaseUser: FirebaseUser }> {
    if (!auth) {
      throw new Error('Firebase not initialized');
    }

    // 1. Sign in with Firebase
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;

    // 2. Get Firebase ID token
    const idToken = await firebaseUser.getIdToken();

    // 3. Verify token with Django and get user data with roles
    const response = await apiClient.post<{ user: User }>(
      '/auth/firebase/verify/',
      { idToken },
      { skipAuth: true }
    );

    console.log('✅ User authenticated with Django:', response.user);

    // 4. Store Firebase token for API calls (using unified TOKEN_KEYS)
    localStorage.setItem(TOKEN_KEYS.FIREBASE, idToken);
    localStorage.setItem(TOKEN_KEYS.USER, JSON.stringify(response.user));

    return {
      user: response.user,
      firebaseUser,
    };
  },

  /**
   * Sign in with Google
   */
  async signInWithGoogle(): Promise<{ user: User; firebaseUser: FirebaseUser }> {
    if (!auth) {
      throw new Error('Firebase not initialized');
    }

    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    const firebaseUser = userCredential.user;

    // Get ID token and verify with Django
    const idToken = await firebaseUser.getIdToken();

    const response = await apiClient.post<{ user: User }>(
      '/auth/firebase/verify/',
      { idToken },
      { skipAuth: true }
    );

    console.log('✅ Google user authenticated:', response.user);

    localStorage.setItem(TOKEN_KEYS.FIREBASE, idToken);
    localStorage.setItem(TOKEN_KEYS.USER, JSON.stringify(response.user));

    return {
      user: response.user,
      firebaseUser,
    };
  },

  /**
   * Sign out - clears ALL auth tokens consistently
   */
  async signOut(): Promise<void> {
    if (!auth) {
      throw new Error('Firebase not initialized');
    }

    await firebaseSignOut(auth);

    // Clear all tokens (tokenManager.clearTokens includes FIREBASE token)
    tokenManager.clearTokens();

    console.log('✅ User signed out');
  },

  /**
   * Send password reset email
   * First verifies email exists in Django, then sends via Firebase
   */
  async sendPasswordReset(email: string): Promise<void> {
    if (!auth) {
      throw new Error('Firebase not initialized');
    }

    // 1. Verify user exists in Django (optional but provides better error messages)
    try {
      await apiClient.post(
        '/auth/firebase/password-reset/',
        { email },
        { skipAuth: true }
      );
    } catch (error: any) {
      // If backend says user doesn't exist, throw that error
      if (error.response?.status === 404) {
        throw new Error('No existe una cuenta con este correo electrónico');
      }
      // For other backend errors, continue and let Firebase handle it
      console.warn('Backend verification failed, trying Firebase directly:', error);
    }

    // 2. Send password reset email via Firebase (this actually sends the email)
    await sendPasswordResetEmail(auth, email);
    console.log('✅ Password reset email sent via Firebase');
  },

  /**
   * Verify password reset code (oobCode from Firebase email link)
   * Returns the email associated with the code if valid
   */
  async verifyResetCode(oobCode: string): Promise<string> {
    if (!auth) {
      throw new Error('Firebase not initialized');
    }

    const email = await verifyPasswordResetCode(auth, oobCode);
    console.log('✅ Reset code verified for:', email);
    return email;
  },

  /**
   * Confirm password reset with new password
   * Uses the oobCode from Firebase email link
   */
  async confirmReset(oobCode: string, newPassword: string): Promise<void> {
    if (!auth) {
      throw new Error('Firebase not initialized');
    }

    await confirmPasswordReset(auth, oobCode, newPassword);
    console.log('✅ Password reset confirmed');
  },

  /**
   * Get current Firebase user
   */
  getCurrentFirebaseUser(): FirebaseUser | null {
    if (!auth) return null;
    return auth.currentUser;
  },

  /**
   * Get current Firebase ID token
   */
  async getCurrentToken(): Promise<string | null> {
    if (!auth || !auth.currentUser) return null;

    try {
      const token = await auth.currentUser.getIdToken();
      localStorage.setItem(TOKEN_KEYS.FIREBASE, token);
      return token;
    } catch (error) {
      console.error('❌ Error getting Firebase token:', error);
      return null;
    }
  },

  /**
   * Refresh Firebase token and sync with Django
   */
  async refreshToken(): Promise<string | null> {
    if (!auth || !auth.currentUser) return null;

    try {
      // Force refresh Firebase token
      const token = await auth.currentUser.getIdToken(true);
      localStorage.setItem(TOKEN_KEYS.FIREBASE, token);

      // Verify with Django to get updated user data
      const response = await apiClient.post<{ user: User }>(
        '/auth/firebase/verify/',
        { idToken: token },
        { skipAuth: true }
      );

      localStorage.setItem(TOKEN_KEYS.USER, JSON.stringify(response.user));

      return token;
    } catch (error) {
      console.error('❌ Error refreshing token:', error);
      return null;
    }
  },

  /**
   * Listen to auth state and token changes
   * Uses onIdTokenChanged instead of onAuthStateChanged because:
   * - onAuthStateChanged: Only fires on sign-in/sign-out
   * - onIdTokenChanged: Also fires when token is refreshed (every ~55 min)
   * This ensures the app always has the latest token
   */
  onAuthStateChange(callback: (firebaseUser: FirebaseUser | null) => void): () => void {
    if (!auth) {
      return () => {};
    }

    return onIdTokenChanged(auth, callback);
  },

  /**
   * Check if Firebase is configured
   */
  isConfigured(): boolean {
    return !!auth;
  },
};
